import { useState, FunctionComponent } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import { colors } from 'ui/theme';
import noop from 'utils/noop';
import { PredictiveGoalsHeader } from '../PredictiveGoalsHeader';
import {
  LayoutWithPanel,
  MainContentLayout,
  MainLayoutWrapper,
  ResultsContainer,
  GoalCriteriaHeader,
  PredictionGoalCriteriaWrapper,
} from '../Styled';
import { createPageActions } from '../createPageActions';
import {
  TEST_IDS,
  GOAL_DELETE_ERROR,
  PREDICTION,
  DELETE_PREDICTIVE_GOALS_TOAST_MESSAGE,
} from '../../constants';
import { GoalDetailsPanel } from '../GoalDetailsPanel';
import { ResultsPageProps, PredictiveGoalModelDetails } from '../../types';
import {
  useGoalManagePermissionVerifier,
  shouldDisableDeleteByStatus,
  shouldDisableEditByStatus,
  toGoalVariables,
} from '../../goalUtils';
import { useGoalDeleteMutation } from '../hooks/useGoalDeleteMutation';
import { DeleteModal } from '../modals/DeleteModal';
import { NoticeBanner } from './NoticeBanner';
import { StatusPageGoalCriteria } from '../StatusPageGoalCriteria';
import { DistributionChartWithRadioSelectors } from '../DistributionChartWithRadioSelectors';
import RefreshingCaption from './RefreshingCaption';

export const ResultsPage: FunctionComponent<React.PropsWithChildren<ResultsPageProps>> = ({
  goal,
  onMutation,
}) => {
  const dispatch = useDispatch();
  const [goalName, setGoalName] = useState(goal.name);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteGoalQuery] = useGoalDeleteMutation(
    (deletedGoal) => {
      (onMutation || noop)(deletedGoal);
      dispatch(addPositiveToast(DELETE_PREDICTIVE_GOALS_TOAST_MESSAGE));
    },
    () => {
      dispatch(addNegativeToast(GOAL_DELETE_ERROR));
    },
  );
  const history = useHistory();
  const canManageGoals = useGoalManagePermissionVerifier();
  const { modelDetails } = goal;
  const goalVariables = toGoalVariables(goal);

  const onDelete = () => {
    setIsDeleteModalOpen(true);
  };
  const onEdit = () => {
    history.push(`/predictive-audiences/goals/${goal.id}/edit`);
  };

  const PageActions = createPageActions({
    isDeleteDisabled: shouldDisableDeleteByStatus(goal),
    isEditDisabled: shouldDisableEditByStatus(goal),
    onDelete,
    onEdit,
    canManageGoals,
  });

  const deleteGoal = () => {
    deleteGoalQuery(goal.id);
    setIsDeleteModalOpen(false);
  };

  const deleteGoalModal = isDeleteModalOpen && (
    <DeleteModal
      isModalOpen={isDeleteModalOpen}
      onClose={() => setIsDeleteModalOpen(false)}
      deleteGoal={deleteGoal}
      goal={goal}
    />
  );

  return (
    <>
      <PredictiveGoalsHeader
        background={colors.white}
        pageActions={<PageActions />}
        editableGoal={goalVariables}
        goalName={goalName}
        onUpdateGoalName={setGoalName}
      />
      <MainLayoutWrapper
        data-background-color={colors.backgroundSecondary}
        data-testid={TEST_IDS.ReadyPage}
      >
        <LayoutWithPanel>
          <GoalDetailsPanel goal={goal} />
          <MainContentLayout>
            <NoticeBanner modelDetails={modelDetails as Required<PredictiveGoalModelDetails>} />
            <StatusPageGoalCriteria goal={goal} />
            <ResultsContainer>
              <PredictionGoalCriteriaWrapper>
                <GoalCriteriaHeader>{PREDICTION}</GoalCriteriaHeader>
              </PredictionGoalCriteriaWrapper>
              <DistributionChartWithRadioSelectors goal={goal} />
            </ResultsContainer>
            <RefreshingCaption goalStatus={goal.status} />
          </MainContentLayout>
        </LayoutWithPanel>
      </MainLayoutWrapper>
      {deleteGoalModal}
    </>
  );
};

export default ResultsPage;
